/**
 * Traduce códigos de error del backend a mensajes en español para mostrar al usuario.
 * Esta función centraliza la traducción de errores para mantener consistencia en toda la aplicación.
 */
export const getErrorMessage = (errorCode: string | undefined): string => {
  if (!errorCode) {
    return "Ha ocurrido un error desconocido. Por favor, intenta nuevamente.";
  }

  switch (errorCode) {
    // Errores de autenticación y autorización
    case "AUTHENTICATION_ERROR":
      return "Error de autenticación. Por favor, inicia sesión nuevamente.";
    case "NOT_AUTHORIZED":
      return "No tienes autorización para realizar esta acción.";
    case "INVALID_PASSWORD":
      return "Contraseña incorrecta. Por favor, verifica tu contraseña.";
    case "PASSWORD_TOO_SHORT":
      return "La contraseña debe tener al menos 8 caracteres.";
    case "SSO_USER_TRYING_TO_LOGIN_WITH_PASSWORD":
      return "Esta cuenta está asociada con un inicio de sesión social. Por favor, usa Google o LinkedIn para iniciar sesión.";

    // Errores de registro de usuario
    case "EMAIL_ALREADY_REGISTERED":
      return "El correo electrónico ya está registrado. Por favor, usa otro correo o inicia sesión.";
    case "PHONE_ALREADY_REGISTERED":
      return "El teléfono ya está registrado en el sistema. Por favor, elige otro teléfono o ponte en contacto con el equipo de Mike.";
    case "USER_ALREADY_EXIST":
      return "El usuario ya existe en el sistema.";
    case "CAPTCHA_VERIFICATION_FAILED":
      return "No hemos podido verificar que eres una persona. Por favor, inténtalo de nuevo.";

    // Errores de negocio y miembros
    case "BUSINESS_NOT_FOUND":
      return "No se encontró la empresa. Por favor, verifica tu cuenta.";
    case "BUSINESS_CLIENT_QUOTA":
      return "Se ha alcanzado la cuota máxima de clientes para tu plan. Por favor, actualiza tu plan para agregar más miembros.";
    case "INVITATION_MEMBER_EXIST":
      return "El usuario o la invitación ya existe para este correo electrónico o teléfono.";
    case "INVITATION_NOT_FOUND":
      return "La invitación no fue encontrada o ha expirado.";
    case "INVITATION_EXPIRED":
      return "La invitación ha expirado. Por favor, solicita una nueva invitación.";
    case "MEMBER_NOT_FOUND":
      return "No se encontró el miembro solicitado.";
    case "MEMBER_ALREADY_ACTIVATED":
      return "Este miembro ya está activado.";
    case "OWNER_CANNOT_BE_INVITED":
      return "El propietario de la empresa no puede ser invitado.";
    case "USER_DELETE_ERROR_LAST_MEMBER":
      return "No se puede eliminar el último miembro de la empresa.";

    // Errores de validación
    case "INVALID_DATA":
      return "Los datos proporcionados no son válidos. Por favor, verifica la información ingresada.";
    case "INVALID_INPUT":
      return "Entrada inválida. Por favor, verifica los datos ingresados.";
    case "INVALID_CODE":
      return "El código proporcionado no es válido.";
    case "BAD_REQUEST":
      return "Solicitud inválida. Por favor, verifica los datos e intenta nuevamente.";

    // Errores de restablecimiento de contraseña
    case "RESET_PASSWORD_INVALID_TOKEN":
      return "El enlace de restablecimiento de contraseña ha expirado o ya fue utilizado. Por favor, solicita un nuevo enlace.";
    case "RESET_PASSWORD_TOKEN_INVALID":
      return "El enlace de restablecimiento no es válido. Por favor, solicita un nuevo enlace.";
    case "RESET_PASSWORD_TOKEN_EXPIRED":
      return "El enlace de restablecimiento ha expirado. Por favor, solicita un nuevo enlace.";

    // Errores de chat
    case "CHAT_NOT_FOUND":
      return "No se encontró el chat solicitado.";
    case "CHAT_ALREADY_CLOSED":
      return "Este chat ya está cerrado.";
    case "CHAT_USER_NOT_FOUND":
      return "No se encontró el usuario del chat.";
    case "USER_NOT_ALLOWED_TO_READ_CHAT":
      return "No tienes permiso para leer este chat.";
    case "BUSINESS_NOT_ALLOWED_TO_CHAT":
      return "Tu empresa no cuenta con una suscripción para realizar esta operación";

    // Errores de integración
    case "INTEGRATION_USER_ALREADY_EXISTS":
      return "El usuario de integración ya existe.";

    // Errores de contacto permitido
    case "ALLOWED_CONTACT_ALREADY_EXISTS":
      return "Este contacto ya está en la lista de contactos permitidos.";
    case "ALLOWED_CONTACT_NOT_FOUND":
      return "No se encontró el contacto permitido.";

    // Errores de cliente/usuario
    case "CLIENT_USER_NOT_FOUND":
      return "No se encontró el usuario solicitado.";
    case "USER_NOT_FOUND":
      return "No se encontró el usuario solicitado.";
    case "USER_NOT_AN_AGENT":
      return "El usuario no es un agente.";
    case "USER_NOT_A_DEVELOPER":
      return "El usuario no es un desarrollador.";

    // Errores de login
    case "NOT_FOUND_LOGIN_INTENT":
      return "No se encontró la intención de inicio de sesión. Por favor, intenta iniciar sesión nuevamente.";

    // Errores de suscripción y pagos
    case "SUBSCRIPTION_NOT_FOUND":
      return "No se encontró la suscripción.";
    case "STRIPE_CUSTOMER_NOT_FOUND":
      return "No se encontró el cliente de pago.";
    case "STRIPE_INVALID_PRODUCT":
      return "El producto de pago no es válido.";

    // Errores de base de datos y servidor
    case "DATABASE_ERROR":
      return "Error en la base de datos. Por favor, intenta nuevamente más tarde.";
    case "INTERNAL_ERROR":
      return "Error interno del servidor. Por favor, intenta nuevamente más tarde.";
    case "EMAIL_SERVICE_ERROR":
      return "Error al enviar el correo electrónico. Por favor, intenta nuevamente más tarde.";

    // Errores de conocimiento
    case "KNOWLEDGE_SHOULD_BE_PENDING_TO_BE_ACTIVATED":
      return "El conocimiento debe estar pendiente para ser activado.";

    // Errores de preguntas
    case "QUESTION_NOT_FOUND":
      return "No se encontró la pregunta solicitada.";

    // Errores de mensajes
    case "MESSAGE_NOT_FOUND":
      return "No se encontró el mensaje solicitado.";
    case "LAST_MESSAGE_IS_NOT_FROM_USER":
      return "El último mensaje no es del usuario.";

    // Errores de WhatsApp
    case "WHATSAPP_LINK_NOT_FOUND":
      return "No se encontró el enlace de WhatsApp.";
    case "ERROR_GETTING_AUDIO":
      return "Error al obtener el audio.";
    case "ERROR_REPLYING_TO_MESSAGE":
      return "Error al responder al mensaje.";

    // Errores de demo
    case "DEMO_FINISHED":
      return "La demo ha finalizado.";

    // Errores de due diligence
    case "DUE_DILIGENCE_CASE_NOT_FOUND":
      return "No se encontró el caso de due diligence.";
    case "DUE_DILIGENCE_CASE_DELETED":
      return "El caso de due diligence ha sido eliminado.";
    case "DUE_DILIGENCE_CASE_NOT_CREATED":
      return "No se pudo crear el caso de due diligence.";

    // Errores de idioma
    case "LANGUAGE_NOT_FOUND":
      return "No se encontró el idioma solicitado.";

    // Errores de traducción
    case "TRANSLATION_ERROR":
      return "Error al traducir el contenido.";

    // Errores genéricos
    case "UNEXPECTED":
      return "Ha ocurrido un error inesperado. Por favor, intenta nuevamente.";
    case "UNKNOWN_ERROR":
      return "Ha ocurrido un error desconocido. Por favor, intenta nuevamente.";

    default:
      return `Error: ${errorCode}. Por favor, contacta al soporte si el problema persiste.`;
  }
};

/**
 * Obtiene un mensaje de error formateado con un prefijo opcional.
 * Útil para casos donde se quiere agregar contexto adicional al mensaje.
 */
export const getFormattedErrorMessage = (
  errorCode: string | undefined,
  prefix?: string
): string => {
  const message = getErrorMessage(errorCode);
  return prefix ? `${prefix}: ${message}` : message;
};

